import React, { useEffect, useState } from "react";
import Clock from 'react-clock';
import Card from "./Card";
import Dialog from "./Dialog";
import { useTick } from "../hooks/useTick";
import { useLocalStorage } from "../hooks/useLocalStorage";

// Import clock styles
import 'react-clock/dist/Clock.css';

export default function ClockCard() {
  const [frozen, setFrozen] = useLocalStorage("clockCard.frozen", false);
  const [storedTime, setStoredTime] = useLocalStorage("clockCard.storedTime", null);
  const [date, setDate] = useState(() => {
    if (storedTime && frozen) {
      return new Date(storedTime);
    }
    return new Date();
  });
  const [isEditOpen, setIsEditOpen] = useState(false);

  const tick = useTick(1000);

  useEffect(() => {
    if (!frozen) {
      setDate(new Date());
      setStoredTime(null);
    }
  }, [frozen, tick, setStoredTime]);

  useEffect(() => {
    if (frozen) {
      setStoredTime(date.getTime());
    }
  }, [date, frozen, setStoredTime]);

  function step(minutes = 1) {
    const newDate = new Date(date.getTime() + minutes * 60000);
    setDate(newDate);
    setFrozen(true);
  }

  function resetToNow() {
    setFrozen(false);
    setDate(new Date());
    setStoredTime(null);
    setIsEditOpen(false);
  }

  return (
    <>
      <Card title="Time" colorIndex={1} onEdit={() => setIsEditOpen(true)}>
        <div className="h-full flex items-center justify-center min-h-0">
          <div className="clock-container w-48 h-48 lg:w-56 lg:h-56 xl:w-64 xl:h-64 2xl:w-72 2xl:h-72">
            <Clock 
              value={date} 
              size="100%"
              hourHandLength={60}
              hourHandWidth={6}
              minuteHandLength={80}
              minuteHandWidth={4}
              secondHandLength={85}
              secondHandWidth={2}
              renderNumbers={true}
              className="react-clock-custom"
            />
          </div>
        </div>
      </Card>

      <Dialog isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} title="Edit Clock">
        <div className="space-y-4">
          <div className="text-center">
            <div className="text-sm font-fredoka text-slate-600 mb-4">
              {frozen ? "Clock is frozen" : "Live time"}
            </div>
          </div>
          
          <div>
            <h3 className="font-fredoka font-bold mb-2">Clock Controls</h3>
            <div className="flex flex-wrap gap-2">
              <button 
                className="px-3 py-2 rounded-lg bg-yellow-200 font-fredoka font-bold shadow flex-1 hover:bg-yellow-300 transition" 
                onClick={() => setFrozen((v) => !v)}
              >
                {frozen ? "Resume" : "Freeze"}
              </button>
            </div>
          </div>

          <div>
            <h3 className="font-fredoka font-bold mb-2">Adjust Time</h3>
            <div className="grid grid-cols-2 gap-2">
              <button 
                className="px-3 py-2 rounded-lg bg-sky-200 font-fredoka font-bold shadow hover:bg-sky-300 transition" 
                onClick={() => step(1)}
              >
                +1 min
              </button>
              <button 
                className="px-3 py-2 rounded-lg bg-sky-200 font-fredoka font-bold shadow hover:bg-sky-300 transition" 
                onClick={() => step(5)}
              >
                +5 min
              </button>
              <button 
                className="px-3 py-2 rounded-lg bg-sky-200 font-fredoka font-bold shadow hover:bg-sky-300 transition" 
                onClick={() => step(15)}
              >
                +15 min
              </button>
              <button 
                className="px-3 py-2 rounded-lg bg-sky-200 font-fredoka font-bold shadow hover:bg-sky-300 transition" 
                onClick={() => step(60)}
              >
                +1 hr
              </button>
            </div>
          </div>

          <div>
            <button 
              className="w-full px-3 py-2 rounded-lg bg-green-200 font-fredoka font-bold shadow hover:bg-green-300 transition" 
              onClick={resetToNow}
            >
              Reset to Current Time
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
